'use client';
import {
  CommentItem,
  ContextUserType,
  IMessage,
  IReplyNavigationInitialFocus,
  ReplyFormPortal,
  useMessageContext,
} from '@ui/components/organisms';
import { CommentOrReplyForm } from './comment-or-reply-form';
import { useEffect, useRef } from 'react';
import { Button, LoadingSkeleton } from '@ui/components';
import { CommentItemSkeleton } from './comment-item-skeleton';
import { ContentType } from '@ui/constants/context';
import { isCurrentCommentThread } from './comment-thread-identity';
import { useThreadPaginationSentinel } from '@ui/hooks/use-thread-pagination-sentinel';

interface Props {
  userType: ContextUserType;
  post: IMessage;
  creator_id: string;
  user_id: string;
  main_post_ref: string;
  enableReply: boolean;
  contentType: ContentType;
  topLevelComments?: boolean;
  isDynamicRoute?: boolean;
  filter_by_comment_id?: string;
  filter_by_reply_id?: string;
  /** A validated focal response used as a route-local presentation seed. */
  initialFocus?: IReplyNavigationInitialFocus | null;
  /** The comment/reply that opened this view, kept stable as pages are loaded. */
  target_comment_id?: string;
  target_reply_id?: string;
  /** External dwell-driven highlight control (see CommentItem). */
  highlight_visible?: boolean;
  highlight_auto_scroll?: boolean;
  /** Route generation used to re-arm scrolling for an explicit same-URL click. */
  highlight_generation_key?: string;
  onTargetVisibilityChange?: (visible: boolean) => void;
  onTargetLayoutChange?: () => void;
  onReplyTargetReadyChange?: (ready: boolean) => void;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  moderators_ids: string[];
}
export const CommentList = (props: Props) => {
  const messageState = useMessageContext(props.userType);
  const initializedThreadRef = useRef<string | null>(null);
  const retainedTargetCommentRef = useRef<{ key: string; comment: IMessage } | null>(null);
  const initialFocusSeedRef = useRef<{ key: string; retired: boolean } | null>(null);
  const targetLiveBaselineRef = useRef<{ key: string; comment?: IMessage }>({
    key: '',
    comment: undefined,
  });
  const initialTargetResponseRef = useRef<{
    key: string;
    phase: 'filtered' | 'anchor' | 'done';
    missing: boolean;
    acceptedAt?: number;
  } | null>(null);

  const paginationSentinelRef = useThreadPaginationSentinel({
    enabled: Boolean(messageState.hasMorePages && !messageState.isMessagesError),
    isFetching: messageState.isFetching,
    onLoadMore: messageState.onNextPage,
    resetKey: `${messageState.query.thread_id}:${
      messageState.query.page ?? messageState.query.anchor_id ?? 1
    }:${messageState.messages.length}`,
  });

  useEffect(() => {
    messageState.onSetThread(props.post.child_thread_id);
    messageState.onSetCreator(props.creator_id);
    messageState.onSetConversation(props.post.conversation_id);
    messageState.onSetFan(props.user_id);
    messageState.onSetFilterByMessage(props.filter_by_comment_id);
    messageState.onSetAnchor(props.filter_by_comment_id ? undefined : props.target_comment_id);

    // Only reset page to 1 if this is a new thread
    if (initializedThreadRef.current !== props.post.child_thread_id) {
      messageState.onSetPage(1);
      initializedThreadRef.current = props.post.child_thread_id;
    }
  }, [
    props.post.child_thread_id,
    props.creator_id,
    props.post.conversation_id,
    props.user_id,
    props.filter_by_comment_id,
    props.filter_by_reply_id,
    props.target_comment_id,
  ]);

  const isCurrentThread = isCurrentCommentThread(messageState.query, props.post, props.creator_id);
  // Passive setters above may lag one render; never expose rows from the previous post.
  if (!isCurrentThread) return null;

  const targetKey = props.target_comment_id
    ? `${props.post.child_thread_id}:${props.post.conversation_id}:${props.creator_id}:${
        props.user_id
      }:${props.target_comment_id}:${props.initialFocus?.generationId ?? 'live'}:${
        props.initialFocus?.viewerId ?? props.user_id
      }`
    : null;
  const initialFocusComment =
    props.initialFocus?.targetType === 'comment'
      ? props.initialFocus.target
      : props.initialFocus?.parentPost;
  const initialFocusSeedKey =
    targetKey && initialFocusComment
      ? `${targetKey}:${props.initialFocus?.generationId ?? ''}:${
          props.initialFocus?.viewerId ?? ''
        }:${initialFocusComment.id}`
      : null;
  if (initialFocusSeedRef.current?.key !== initialFocusSeedKey) {
    initialFocusSeedRef.current = initialFocusSeedKey
      ? { key: initialFocusSeedKey, retired: false }
      : null;
  }
  if (initialTargetResponseRef.current?.key !== targetKey) {
    initialTargetResponseRef.current = targetKey
      ? {
          key: targetKey,
          phase: props.filter_by_comment_id ? 'filtered' : 'anchor',
          missing: false,
          acceptedAt: props.initialFocus?.acceptedAt,
        }
      : null;
  }
  const pinnedComments = messageState.pinned_messages ?? [];
  const pinnedCommentIds = new Set(pinnedComments.map((comment) => comment.id));
  const listedComments = messageState.messages.filter(
    (comment) => !pinnedCommentIds.has(comment.id),
  );
  const loadedTargetComment = props.target_comment_id
    ? messageState.messages.find((comment) => comment.id === props.target_comment_id) ??
      pinnedComments.find((comment) => comment.id === props.target_comment_id)
    : undefined;
  const targetLiveKey = `${targetKey || ''}:${props.initialFocus?.acceptedAt ?? ''}`;
  const targetLiveChangedAfterSeed =
    targetLiveBaselineRef.current.key === targetLiveKey &&
    loadedTargetComment !== undefined &&
    targetLiveBaselineRef.current.comment !== undefined &&
    loadedTargetComment !== targetLiveBaselineRef.current.comment;
  if (targetLiveBaselineRef.current.key !== targetLiveKey) {
    targetLiveBaselineRef.current = { key: targetLiveKey, comment: loadedTargetComment };
  } else if (loadedTargetComment !== targetLiveBaselineRef.current.comment) {
    targetLiveBaselineRef.current.comment = loadedTargetComment;
  }
  const liveTargetCommentIsFresh = Boolean(
    !props.initialFocus?.acceptedAt ||
      targetLiveChangedAfterSeed ||
      messageState.fulfilledTimeStamp === undefined ||
      messageState.fulfilledTimeStamp >= props.initialFocus.acceptedAt,
  );
  const isInitialTargetRequest = Boolean(
    targetKey &&
      initialTargetResponseRef.current &&
      initialTargetResponseRef.current.phase !== 'done' &&
      props.target_comment_id &&
      ((initialTargetResponseRef.current.phase === 'filtered' &&
        messageState.query.message_ids === props.target_comment_id) ||
        (initialTargetResponseRef.current.phase === 'anchor' &&
          messageState.query.anchor_id === props.target_comment_id)),
  );
  const initialTargetResponseIsFresh = Boolean(
    !initialTargetResponseRef.current?.acceptedAt ||
      messageState.fulfilledTimeStamp === undefined ||
      messageState.fulfilledTimeStamp >= initialTargetResponseRef.current.acceptedAt,
  );
  if (
    isInitialTargetRequest &&
    messageState.isSuccess &&
    !messageState.isFetching &&
    initialTargetResponseIsFresh &&
    initialTargetResponseRef.current
  ) {
    const missing = !loadedTargetComment;
    initialTargetResponseRef.current.missing = missing;
    if (missing && initialFocusSeedRef.current) {
      initialFocusSeedRef.current.retired = true;
    }
    initialTargetResponseRef.current.phase =
      missing || initialTargetResponseRef.current.phase === 'anchor' ? 'done' : 'anchor';
  }
  const targetComment = props.target_comment_id
    ? (liveTargetCommentIsFresh ? loadedTargetComment : undefined) ||
      (initialFocusSeedRef.current?.retired ? undefined : initialFocusComment)
    : undefined;
  const initialTargetResponseMissing = Boolean(
    targetKey &&
      initialTargetResponseRef.current?.key === targetKey &&
      initialTargetResponseRef.current.missing,
  );
  if (!targetKey || messageState.isMessagesError || initialTargetResponseMissing) {
    retainedTargetCommentRef.current = null;
  } else if (targetComment) {
    retainedTargetCommentRef.current = { key: targetKey, comment: targetComment };
  } else if (retainedTargetCommentRef.current?.key !== targetKey) {
    retainedTargetCommentRef.current = null;
  }

  const retainedTargetComment =
    retainedTargetCommentRef.current?.key === targetKey
      ? retainedTargetCommentRef.current.comment
      : undefined;
  const replaceStaleTarget = (comment: IMessage) =>
    retainedTargetComment && !liveTargetCommentIsFresh && comment.id === retainedTargetComment.id
      ? retainedTargetComment
      : comment;
  const renderedPinnedComments = pinnedComments.map(replaceStaleTarget);
  const renderedComments =
    retainedTargetComment && !pinnedCommentIds.has(retainedTargetComment.id)
      ? listedComments.some((comment) => comment.id === retainedTargetComment.id)
        ? listedComments.map(replaceStaleTarget)
        : [retainedTargetComment, ...listedComments]
      : listedComments;
  const currentPage = messageState.query.page ?? messageState.anchorPage ?? 1;
  const hasEarlierComments = Boolean(
    messageState.query.anchor_id &&
      (messageState.minLoadedPage ?? currentPage) > 1 &&
      messageState.onPreviousPage,
  );
  const paginationVersion = `${messageState.query.thread_id ?? ''}:${
    messageState.query.anchor_id ?? ''
  }:${messageState.query.page ?? ''}:${props.filter_by_comment_id ?? 'all'}:${
    renderedComments.length
  }`;
  const shouldHighlightTarget = Boolean(
    props.target_comment_id &&
      (!props.filter_by_comment_id || props.filter_by_comment_id === props.target_comment_id),
  );

  if (messageState.isLoading && renderedComments.length === 0) {
    return (
      <div className="flex flex-col gap-y-6 pt-4">
        <LoadingSkeleton className=" m-w-[70vw] h-7 w-full rounded-sm" />
        <CommentItemSkeleton />
        <CommentItemSkeleton />
      </div>
    );
  }

  const renderRetry = (label: string) => (
    <div className="flex flex-col items-center justify-center gap-2 py-4" role="alert">
      <span className="text-sm text-korDarkGrey">Unable to load {label}.</span>
      <Button
        type="secondary"
        size="small"
        onClick={() => void messageState.refetchMessages()}
        disabled={messageState.isFetching}>
        Try again
      </Button>
    </div>
  );

  return (
    <>
      <ReplyFormPortal
        {...props}
        queryParams={messageState.query}
        community_details={
          props.post.community_details as NonNullable<IMessage['community_details']>
        }
        creatorData={props.creatorData}
      />
      <div data-community-comment-list className="space-y-4 mb-4">
        {!props.filter_by_comment_id && (
          <CommentOrReplyForm
            creator_id={props.post.creator_id}
            thread_id={props.post.child_thread_id}
            user_id={messageState.user_id}
            contentType={props.contentType === 'comment' ? 'reply' : 'comment'}
            userType={props.userType}
            activateToolbarOnFocus
            createThreadIfNotExist
            message_id={props.post.id}
            conversation_id={props.post.conversation_id}
            queryParam={messageState.query}
            main_post_ref={props.main_post_ref}
            community_details={
              props.post.community_details as NonNullable<IMessage['community_details']>
            }
            creatorData={props.creatorData}
          />
        )}

        {renderedPinnedComments.length > 0 && (
          <div className="mb-4 space-y-2 flex items-start flex-col w-full border-b border-b-orange-100 relative pb-5">
            {renderedPinnedComments.map((comment) => (
              <div key={comment.id} className="relative w-full">
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  userType={props.userType}
                  user_id={messageState.user_id}
                  creator_id={props.creator_id || comment.creator_id}
                  enableReply={props.enableReply}
                  reportLabel="Report comment"
                  isReply={false}
                  isTopLevelComment={props.topLevelComments}
                  query={messageState.query}
                  main_post_ref={props.main_post_ref}
                  isPinned
                  communityDetails={messageState.community_details || props.post.community_details}
                  isAuthenticated={messageState.isAuthenticated}
                  isFanOfCreator={messageState.isFanOfCreator}
                  hierarchyType="comment"
                  {...(props.filter_by_reply_id &&
                    comment.id === props.target_comment_id && {
                      filter_by_reply_id: props.filter_by_reply_id,
                      keepThreadLineOpen: true,
                    })}
                  initialFocus={props.initialFocus}
                  target_comment_id={props.target_comment_id}
                  target_reply_id={
                    comment.id === props.target_comment_id ? props.target_reply_id : undefined
                  }
                  highlight_target={shouldHighlightTarget}
                  highlight_visible={props.highlight_visible}
                  highlight_auto_scroll={props.highlight_auto_scroll}
                  highlight_generation_key={props.highlight_generation_key}
                  onTargetVisibilityChange={props.onTargetVisibilityChange}
                  onTargetLayoutChange={props.onTargetLayoutChange}
                  onReplyTargetReadyChange={
                    comment.id === props.target_comment_id
                      ? props.onReplyTargetReadyChange
                      : undefined
                  }
                  pagination_version={paginationVersion}
                  moderators_ids={props.moderators_ids || []}
                />
              </div>
            ))}
          </div>
        )}

        <div className="pb-10 space-y-2">
          {hasEarlierComments && !messageState.isFetching && !messageState.isMessagesError && (
            <div className="flex justify-center py-2">
              <Button type="secondary" size="small" onClick={() => messageState.onPreviousPage?.()}>
                Load earlier comments
              </Button>
            </div>
          )}

          {renderedComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              userType={props.userType}
              user_id={messageState.user_id}
              // creator_id={comment.creator_id || props.creator_id}
              creator_id={props.creator_id || comment.creator_id}
              enableReply={props.enableReply}
              reportLabel="Report comment"
              isReply={false}
              isTopLevelComment={props.topLevelComments}
              query={messageState.query}
              main_post_ref={props.main_post_ref}
              isDynamicRoute={props.isDynamicRoute}
              communityDetails={messageState.community_details || props.post.community_details}
              isAuthenticated={messageState.isAuthenticated}
              isFanOfCreator={messageState.isFanOfCreator}
              hierarchyType="comment"
              {...(props.filter_by_reply_id &&
                comment.id === props.target_comment_id && {
                  filter_by_reply_id: props.filter_by_reply_id,
                  keepThreadLineOpen: true,
                })}
              initialFocus={props.initialFocus}
              target_comment_id={props.target_comment_id}
              target_reply_id={
                comment.id === props.target_comment_id ? props.target_reply_id : undefined
              }
              highlight_target={shouldHighlightTarget}
              highlight_visible={props.highlight_visible}
              highlight_auto_scroll={props.highlight_auto_scroll}
              highlight_generation_key={props.highlight_generation_key}
              onTargetVisibilityChange={props.onTargetVisibilityChange}
              onTargetLayoutChange={props.onTargetLayoutChange}
              onReplyTargetReadyChange={
                comment.id === props.target_comment_id ? props.onReplyTargetReadyChange : undefined
              }
              pagination_version={paginationVersion}
              moderators_ids={props.moderators_ids || []}
            />
          ))}

          {messageState.isFetching && messageState.messages.length > 0 && (
            <div className="flex flex-col gap-y-6 pt-4" aria-live="polite">
              <CommentItemSkeleton />
            </div>
          )}

          {messageState.isMessagesError && renderRetry('comments')}

          <div ref={paginationSentinelRef} aria-hidden="true" className="min-h-px" />

          {messageState.hasMorePages &&
            !messageState.isMessagesError &&
            !messageState.isFetching && (
              <div className="flex justify-center py-4">
                <Button type="secondary" size="small" onClick={messageState.onNextPage}>
                  Load more comments
                </Button>
              </div>
            )}
        </div>
      </div>
    </>
  );
};
